import Blogimageplaceholder from "../../../../../assets/blogplaceholder.webp";
import { blogAssetUrl, BLOG_API_ORIGIN } from "@/lib/blogApi";

/**
 * Fetch a single blog by numeric id or short-title slug — mirrors
 * BlogDetails.jsx's own lookup: a purely-numeric last segment of the URL
 * slug is treated as an id (GET /blog/:id), anything else is decoded back
 * into its space-separated short title (GET /blog/check/:shortTitle).
 */
export const getBlogBySlug = async (titleSlug) => {
  const lastPart = titleSlug?.split("-").pop();
  const id = /^\d+$/.test(lastPart) ? lastPart : null;
  const url = id
    ? `${BLOG_API_ORIGIN}/blog/${id}`
    : `${BLOG_API_ORIGIN}/blog/check/${encodeURIComponent(decodeURIComponent(titleSlug).replace(/-/g, " "))}`;
  const response = await fetch(url, { next: { revalidate: 300 } });
  // The backend answers an unknown blog with 404 and an empty body (and a malformed id with 400): that is
  // "no such blog". Any other failure (5xx, gateway errors) must not look like "not found" - the blog page is
  // cached (ISR), and a transient backend error would otherwise be cached as a 404 for minutes.
  if (response.status === 404 || response.status === 400) return null;
  if (!response.ok) throw new Error(`Blog lookup failed (${response.status})`);
  const text = await response.text();
  if (!text) return null;
  return JSON.parse(text) || null;
};

/**
 * Fetch every blog (object-shaped, full list) — used for the sidebar
 * "Recent Post" list, Previous/Next navigation, and Related Blogs, all of
 * which are derived client-side from this same array in CRA's
 * BlogDetails.jsx, not separate endpoints.
 */
export const getAllBlogs = async () => {
  const response = await fetch(`${BLOG_API_ORIGIN}/blog`);
  if (!response.ok) throw new Error("Failed to fetch blogs");
  const data = await response.json();
  if (!Array.isArray(data)) return [];
  const mapped = data.map((blog) => ({
    ...blog,
    blog_image: blog.blog_image
      ? blogAssetUrl(blog.blog_image)
      : Blogimageplaceholder.src,
  }));
  return mapped.sort((a, b) => new Date(b.date) - new Date(a.date));
};

/**
 * The blog list the blog-detail page needs in the browser (sidebar "Recent Post", search, related posts),
 * built from GET /blog/categories + GET /blog/filter (~52 KB for every blog) instead of GET /blog, whose
 * full entities include every article body (~5.4 MB, uncompressed). Same fields, mapping and newest-first
 * order getAllBlogs() gives for what that page reads (id, title, category_name, shortTitle, author, date,
 * blog_image, authorImage); verified identical for all 247 blogs. `description` is not included - nothing
 * on the page reads it from this list. getAllBlogs() is unchanged (sitemap.js still uses it).
 */
export const getAllBlogsForDetailPage = async () => {
  const categoriesRes = await fetch(`${BLOG_API_ORIGIN}/blog/categories`);
  if (!categoriesRes.ok) throw new Error("Failed to fetch blog categories");
  const categories = await categoriesRes.json();
  if (!Array.isArray(categories) || categories.length === 0) return [];

  const params = new URLSearchParams();
  categories.forEach((c) => params.append("category", c));
  const blogsRes = await fetch(`${BLOG_API_ORIGIN}/blog/filter?${params.toString()}`);
  if (!blogsRes.ok) throw new Error("Failed to fetch blogs");
  const rows = await blogsRes.json();
  if (!Array.isArray(rows)) return [];

  return rows
    .map((row) => {
      const [id, category_name, title, short_title, author, author_image, blog_image, date] = row;
      return {
        id,
        category_name,
        title,
        shortTitle: short_title,
        author,
        authorImage: author_image,
        date,
        blog_image: blog_image ? blogAssetUrl(blog_image) : Blogimageplaceholder.src,
      };
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));
};

/**
 * Fetch every blog, but as the same lightweight row-tuple shape
 * getRecentBlogs()/BlogList.jsx's own per-category fetch use (id,
 * category_name, title, short_title, ...) — no `description` field. Used
 * where only enough data to build a /blogs/:category/:slug link is needed
 * (e.g. the /blogs listing page's crawlable link list), not the full
 * article body.
 *
 * There is no backend "list all blogs, lightweight" endpoint; GET /blog
 * returns the full entity (including `description`) for all ~224 rows,
 * which measured at a ~7MB response and 30-45s — too slow to run on every
 * request and too large for Next's fetch cache (2MB cap) to help. Instead
 * this reuses GET /blog/filter (the same lightweight, no-description
 * endpoint BlogList.jsx already calls per-category) with every known
 * category passed at once, since its backing query is `WHERE category_name
 * IN (:categories)` — equivalent to "all blogs" without the heavy payload.
 */
export const getAllBlogsLightweight = async () => {
  const categoriesRes = await fetch(`${BLOG_API_ORIGIN}/blog/categories`, { next: { revalidate: 300 } });
  if (!categoriesRes.ok) return [];
  const categories = await categoriesRes.json();
  if (!Array.isArray(categories) || categories.length === 0) return [];

  const params = new URLSearchParams();
  categories.forEach((c) => params.append("category", c));
  const blogsRes = await fetch(`${BLOG_API_ORIGIN}/blog/filter?${params.toString()}`, { next: { revalidate: 300 } });
  if (!blogsRes.ok) return [];
  const rows = await blogsRes.json();
  if (!Array.isArray(rows)) return [];

  // author/date/avatar/blog_image are the same fields (and the same mapping) BlogList.jsx builds from
  // this endpoint's rows, so /blogs can render its default view from this one server-side response.
  return rows.map((row) => {
    const [id, category_name, title, short_title, author, author_image, blog_image, date] = row;
    return {
      id,
      category_name,
      title,
      shortTitle: short_title,
      author,
      date,
      avatar: author_image ? blogAssetUrl(author_image) : "",
      blog_image: blog_image ? blogAssetUrl(blog_image) : "",
    };
  });
};

/**
 * Fetch recent blog posts
 * Returns transformed blog data with full image URLs
 */
export const getRecentBlogs = async () => {
  const response = await fetch(`${BLOG_API_ORIGIN}/blog/recent`);
  if (!response.ok) {
    throw new Error("Failed to fetch recent blogs");
  }
  const data = await response.json();
  if (!Array.isArray(data)) {
    throw new Error("Invalid blog data format");
  }

  // Transform array response to object format. shortTitle (camelCase) to
  // match every other blog object this service produces (getBlogBySlug,
  // getAllBlogsLightweight) and what src/lib/blogUrl.js's shared
  // getBlogPath() expects — this function used to be the one place that
  // kept the raw snake_case short_title, silently breaking short-title
  // links for anything built from this endpoint's data.
  return data.map(row => {
    const [id, category_name, title, short_title, author, author_image, blog_image, date] = row;
    const avatar = author_image ? blogAssetUrl(author_image) : "";
    const blogImg = blog_image ? blogAssetUrl(blog_image) : "";
    return {
      id,
      category_name,
      title,
      shortTitle: short_title,
      author,
      date,
      avatar,
      blog_image: blogImg
    };
  });
};
